import addDays from 'date-fns/addDays'
import tlds from 'tlds'
import {
  ResponseProductMinimumRates,
  Targeting,
  TPBudget,
  UTM,
} from '@ads/front-core/api'
import { Capping, Device } from '@ads/front-core/constants/campaigns'
import { POLAND_COUNTRY_CODE } from '@ads/front-core/constants/countryCodes'
import limits from '@ads/front-core/constants/limits'
import { lng } from '@ads/front-core/locales'
import Joi, { CustomHelpers } from '@ads/front-core/utils/joi'
import { TDictionary, IExtendedDictionary } from '../interfaces'

const CPC_MAX_DAYS_CAMPAIGN = 180

const CPM_MAX_DAYS_CAMPAIGN = 180

export const INCORRECT_TARGET_URL_CODE = 'Incorrect targetUrl'

type TExtendedCityDictionary = IExtendedDictionary<{
  regionId: number
}>

export const urlValidation = Joi.url()

export const joiNameValidation = Joi.string()
  .max(limits.CAMPAIGN_NAME_MAX_LENGTH)
  .min(1)
export const dateStartValidation = Joi.date().iso()
export const timezoneValidation = Joi.string()
export const dateEndValidation = Joi.date().when('dateStart', {
  is: Joi.date().exist(),
  then: Joi.date()
    .iso()
    .min(Joi.ref('dateStart'))
    .message(
      lng('errors:dateMin').replace('{#limit}', lng('calendar:startDate')),
    ),
  otherwise: Joi.date().iso().min(new Date()),
})
export const dateEndValidationCPC = Joi.date().when('dateStart', {
  is: Joi.date().exist(),
  then: Joi.date()
    .iso()
    .min(Joi.ref('dateStart'))
    .message(
      lng('errors:dateMin').replace('{#limit}', lng('calendar:startDate')),
    )
    .max(
      Joi.ref('dateStart', {
        adjust: val => addDays(val, CPC_MAX_DAYS_CAMPAIGN),
      }),
    )
    .message(
      lng('errors:campaignCanNotLastMoreThanXDays', {
        arg: CPC_MAX_DAYS_CAMPAIGN,
      }),
    ),
  otherwise: Joi.date().iso().min(new Date()),
})
export const dateEndValidationCPM = Joi.date().when('dateStart', {
  is: Joi.date().exist(),
  then: Joi.date()
    .iso()
    .min(Joi.ref('dateStart'))
    .message(
      lng('errors:dateMin').replace('{#limit}', lng('calendar:startDate')),
    )
    .max(
      Joi.ref('dateStart', {
        adjust: val => addDays(val, CPM_MAX_DAYS_CAMPAIGN),
      }),
    )
    .message(
      lng('errors:campaignCanNotLastMoreThanXDays', {
        arg: CPM_MAX_DAYS_CAMPAIGN,
      }),
    ),
  otherwise: Joi.date().iso().min(new Date()),
})

export const cappingFieldsValidation = Joi.object({
  maxDateTimeCount: Joi.number().min(0).empty(''),
  maxViewsCount: Joi.number().min(0).max(20).empty(''),
  timeUnit: Joi.allow(...Object.values(Capping)),
})
  .with('maxDateTimeCount', 'maxViewsCount')
  .with('maxViewsCount', 'maxDateTimeCount')
  .messages({ 'object.with': lng('errors:bothFieldsMustBeFilledIn') })

export const cappingValidation = Joi.object({
  desktop: Joi.when(Joi.ref('...targeting.device.included'), {
    is: Joi.array().items('MOBILE', 'ANY'),
    then: Joi.allow(null).optional(),
    otherwise: cappingFieldsValidation,
  }),
  mobile: Joi.when(Joi.ref('...targeting.device.included'), {
    is: Joi.array().items('DESKTOP', 'ANY'),
    then: Joi.allow(null).optional(),
    otherwise: cappingFieldsValidation,
  }),
  any: cappingFieldsValidation,
})

const targetingGender = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().length(0),
})
const targetingAge = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().length(0),
})
export const targetingCities = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().items(Joi.number()),
}).xor('included', 'excluded')
export const targetingCountries = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().items(Joi.number()),
}).xor('included', 'excluded')
export const targetingRegions = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().items(Joi.number()),
}).xor('included', 'excluded')
export const targetingSegments = Joi.object({
  included: Joi.array().items(Joi.string()),
  excluded: Joi.array().items(Joi.string()),
})

const customValidateCities = (
  value: number[],
  helpers: CustomHelpers,
  dictionaryCity: TExtendedCityDictionary[],
) => {
  const ancestor = helpers.state.ancestors[1]
  let regionsIds = []

  if (ancestor.regions?.included?.length) {
    regionsIds = ancestor.regions.included
  } else if (ancestor.regions?.excluded?.length) {
    regionsIds = ancestor.regions.excluded
  }

  if (!regionsIds.length || !value.length) {
    return value
  }

  const selectedCitiesItems = dictionaryCity.filter(city =>
    value.includes(Number(city.id)),
  )

  const regionsAndCitiesMatch = selectedCitiesItems.every(city =>
    regionsIds.some(regionId => regionId === city.metadata.regionId),
  )

  if (!regionsAndCitiesMatch) {
    const citiesNames = selectedCitiesItems
      .filter(
        city =>
          !regionsIds.find(regionId => regionId === city.metadata.regionId),
      )
      .map(city => city.value)
    return helpers.message({
      custom: lng('campaign:errorCitiesTargeting', {
        arg: citiesNames.join(', '),
      }),
    })
  }

  return value
}

const customValidateRegions = (
  value: number[],
  helpers: CustomHelpers,
  dictionaryCity: TExtendedCityDictionary[],
  dictionaryRegion?: TDictionary[],
) => {
  const ancestor = helpers.state.ancestors[1]
  let citiesIds = []

  if (ancestor.cities?.included?.length) {
    citiesIds = ancestor.cities.included
  } else if (ancestor.cities?.excluded?.length) {
    citiesIds = ancestor.cities.excluded
  }

  if (!citiesIds.length || !value.length) {
    return value
  }

  const cities = dictionaryCity.filter(city =>
    citiesIds.includes(Number(city.id)),
  )

  const regionsAndCitiesMatch = value.every(regionId =>
    cities.some(city => city.metadata.regionId === regionId),
  )

  if (!regionsAndCitiesMatch) {
    const regionsNames = dictionaryRegion
      .filter(region => value.includes(Number(region.id)))
      .filter(
        region => !cities.find(city => city.metadata.regionId === region.id),
      )
      .map(region => region.value)
    return helpers.message({
      custom: lng('campaign:errorRegionsTargeting', {
        arg: regionsNames.join(', '),
      }),
    })
  }

  return value
}

export const getTargetingCities = (dictionaryCity: TExtendedCityDictionary[]) =>
  Joi.object({
    included: Joi.array()
      .items(Joi.number())
      .custom((value, helpers) =>
        customValidateCities(value, helpers, dictionaryCity),
      ),
    excluded: Joi.array()
      .items(Joi.number())
      .custom((value, helpers) =>
        customValidateCities(value, helpers, dictionaryCity),
      ),
  })

export const getTargetingRegions = (
  dictionaryCity: TExtendedCityDictionary[],
  dictionaryRegion: TDictionary[],
) =>
  Joi.object({
    included: Joi.array()
      .items(Joi.number())
      .custom((value, helpers) =>
        customValidateRegions(value, helpers, dictionaryCity, dictionaryRegion),
      ),
    excluded: Joi.array()
      .items(Joi.number())
      .custom((value, helpers) =>
        customValidateRegions(value, helpers, dictionaryCity, dictionaryRegion),
      ),
  })

const targetingRetargetingSegments = Joi.object({
  included: Joi.array().items(Joi.string()),
  excluded: Joi.array().items(Joi.string()),
})
const targetingTopic = Joi.object({
  included: Joi.array().items(Joi.number()),
  excluded: Joi.array().length(0),
})
export const targetingDomain = Joi.object({
  included: Joi.array().items(Joi.string().domain()),
  excluded: Joi.array().items(Joi.string().domain()),
}).xor('included', 'excluded')
const targetingRekIds = Joi.object({
  included: Joi.array().items(Joi.string()).min(1),
})
export const targetingSlots = Joi.object({
  included: Joi.array().items(Joi.number()),
})
export const targetingSlotsMin = Joi.object({
  included: Joi.array()
    .items(Joi.number())
    .min(1)
    .message(lng('errors:fieldIsRequired')),
})
export const targetingDevice = Joi.object({
  included: Joi.array()
    .items(...Object.values(Device))
    .min(1),
  excluded: Joi.array().length(0),
})

export const getTargetingValidation = (
  dictionaryCity: TExtendedCityDictionary[],
  dictionaryRegion: TDictionary[],
) =>
  targetingValidation.keys({
    regions: getTargetingRegions(dictionaryCity, dictionaryRegion),
    cities: getTargetingCities(dictionaryCity),
  })

export const targetingValidation = Joi.object({
  gender: targetingGender,
  age: targetingAge,
  device: Joi.object({
    included: Joi.array().items(...Object.values(Device)),
    excluded: Joi.array().length(0),
  }),
  cities: targetingCities,
  countries: targetingCountries,
  regions: targetingRegions,
  segments: targetingSegments,
  retargetingSegments: targetingRetargetingSegments,
  topic: targetingTopic,
  domain: targetingDomain,
  iabCategories: Joi.targetingIAB(),
})

export const getTargetingWithDeviceValidation = (
  dictionaryCity: TExtendedCityDictionary[],
  dictionaryRegion: TDictionary[],
) =>
  targetingWithDeviceValidation.keys({
    regions: getTargetingRegions(dictionaryCity, dictionaryRegion),
    cities: getTargetingCities(dictionaryCity),
  })

export const targetingWithDeviceValidation = Joi.object({
  gender: targetingGender,
  age: targetingAge,
  device: targetingDevice,
  cities: targetingCities,
  countries: targetingCountries,
  regions: targetingRegions,
  segments: targetingSegments,
  retargetingSegments: targetingRetargetingSegments,
  topic: targetingTopic,
  domain: targetingDomain,
  iabCategories: Joi.targetingIAB(),
})

export const getTargetingPublishersValidation = (
  dictionaryCity: TExtendedCityDictionary[],
  dictionaryRegion: TDictionary[],
) =>
  targetingPublishersValidation.keys({
    regions: getTargetingRegions(dictionaryCity, dictionaryRegion),
    cities: getTargetingCities(dictionaryCity),
  })

export const targetingPublishersValidation = Joi.object({
  age: targetingAge,
  gender: targetingGender,
  device: targetingDevice,
  countries: targetingCountries,
  cities: targetingCities,
  regions: targetingRegions,
  segments: targetingSegments,
  rekids: targetingRekIds,
  slots: targetingSlots,
})

export const targetingScreeningPublishersValidation = targetingPublishersValidation.keys(
  {
    device: Joi.object({
      included: Joi.array().length(0),
      excluded: Joi.array().length(0),
    }),
  },
)

export const getBudgetValidationDesktopMobile = ({
  minDailyBudget,
  minBudget,
  minRateDesktop,
  minRateMobile,
  monthlyCost,
  isCampaignStarted,
  customRates,
}: {
  minDailyBudget: number
  minBudget: number
  minRateDesktop: number
  minRateMobile: number
  monthlyCost: number
  isCampaignStarted: boolean
  customRates?: ResponseProductMinimumRates
}) =>
  Joi.object<Record<string, TPBudget>>({
    DESKTOP: Joi.object({
      rate: Joi.when(Joi.ref('....targeting.device.included'), {
        is: Joi.array().items('MOBILE'),
        then: Joi.allow('', null).optional(),
        otherwise: Joi.number()
          .min(customRates?.valueDesktopMin || minRateDesktop)
          .max(customRates?.valueDesktopMax || limits.MAX_RATE)
          .required(),
      }),
      dailyLimit: Joi.number()
        .integer()
        .allow('', null)
        .min(minDailyBudget)
        .optional(),
      globalLimit: Joi.when(Joi.ref('....targeting.device.included'), {
        is: Joi.array().items('MOBILE'),
        then: Joi.allow('', null).optional(),
        otherwise: Joi.number()
          .integer()
          .min(minBudget)
          .max(limits.CAMPAIGN_MAX_LIMIT)
          .required(),
      }),
    }),
    MOBILE: Joi.object({
      rate: Joi.when(Joi.ref('....targeting.device.included'), {
        is: Joi.array().items('DESKTOP'),
        then: Joi.allow('', null).optional(),
        otherwise: Joi.number()
          .min(customRates?.valueMobileMin || minRateMobile)
          .max(customRates?.valueMobileMax || limits.MAX_RATE)
          .required(),
      }),
      dailyLimit: Joi.number()
        .integer()
        .allow('', null)
        .min(minDailyBudget)
        .optional(),
      globalLimit: Joi.when(Joi.ref('....targeting.device.included'), {
        is: Joi.array().items('DESKTOP'),
        then: Joi.allow('', null).optional(),
        otherwise: Joi.number()
          .integer()
          .min(minBudget)
          .max(limits.CAMPAIGN_MAX_LIMIT)
          .required(),
      }),
    }),
  })
    .custom((value, helpers) => {
      if (!isCampaignStarted) {
        return value
      }

      const deviceTargeting =
        helpers.state.ancestors[0].targeting?.device?.included || []

      const desktopRate =
        (deviceTargeting.includes('DESKTOP') && value.DESKTOP?.globalLimit) || 0
      const mobileRate =
        (deviceTargeting.includes('MOBILE') && value.MOBILE?.globalLimit) || 0

      const sumRate = desktopRate + mobileRate

      if (monthlyCost > sumRate) {
        return helpers.error('number.sumGlobalLimitMax', {
          limit: monthlyCost,
        })
      }

      return value
    })
    .messages({
      'number.sumGlobalLimitMax': lng(
        'campaign:budgetIsLowerThanCampaignCost',
        { monthlyCost },
      ),
    })

export const getBudgetValidationAny = ({
  minDailyBudget,
  minBudget,
  minRate,
  maxRate,
  monthlyCost,
  isCampaignStarted,
  customRates,
}: {
  minDailyBudget: number
  minBudget: number
  minRate: number
  maxRate?: number
  monthlyCost?: number
  isCampaignStarted?: boolean
  customRates?: ResponseProductMinimumRates
}) =>
  Joi.object<Record<string, TPBudget>>({
    ANY: Joi.object({
      rate: Joi.number()
        .min(customRates?.valueMin || minRate || 0)
        .max(customRates?.valueMax || maxRate || limits.CAMPAIGN_MAX_LIMIT)
        .required(),
      dailyLimit: Joi.number()
        .integer()
        .allow('', null)
        .min(minDailyBudget)
        .max(limits.CAMPAIGN_MAX_LIMIT)
        .optional(),
      globalLimit: Joi.number()
        .integer()
        .min(minBudget)
        .max(limits.CAMPAIGN_MAX_LIMIT)
        .required()
        .custom((value, helpers) => {
          if (!isCampaignStarted) {
            return value
          }

          const globalLimit = value || 0
          if (monthlyCost > globalLimit) {
            return helpers.error('number.sumGlobalLimitMax', {
              limit: monthlyCost,
            })
          }

          return value
        })
        .messages({
          'number.sumGlobalLimitMax': lng(
            'campaign:budgetIsLowerThanCampaignCost',
            { monthlyCost },
          ),
        }),
    }),
  })

export const mailFromValidation = Joi.string()
  .regex(new RegExp('^[^@]*$'))
  .max(limits.MAIL_FROM_MAX_LENGTH)

export const mailAddressValidation = Joi.string()
  .email({
    tlds: { allow: tlds },
  })
  .max(limits.MAILING_TO_MAX_LENGTH)

export const mailFooterValidation = Joi.string().allow('', null).max(5000)

export const mailTitleValidation = Joi.textField().min(1)

export const pixelIdValidation = Joi.textField().pattern(/^([A-Za-z0-9-_.]+)$/)

export const targetingFormValidation = (
  val: Targeting,
): { message: string; valid: boolean } => {
  const valid =
    (val?.topic?.included || []).length > 0 ||
    (val?.topic?.excluded || []).length > 0 ||
    (val?.retargetingSegments?.included || []).length > 0 ||
    (val?.retargetingSegments?.excluded || []).length > 0 ||
    (val?.segments?.included || []).length > 0 ||
    (val?.segments?.excluded || []).length > 0
  return {
    message: lng('errors:campaignWithoutTargeting'),
    valid,
  }
}

export const joiNipValidation = (country: string) =>
  Joi.when('countryId', {
    is: country,
    then: Joi.when('countryId', {
      is: POLAND_COUNTRY_CODE,
      then: Joi.nip()
        .required()
        .messages({
          'any.required': lng('registration:nipRequired'),
        }),
      otherwise: Joi.string()
        .alphanum()
        .required()
        .messages({
          'any.required': lng('registration:viesRequired'),
          'string.alphanum': lng('registration:viesIncorrect'),
        }),
    }),
    otherwise: Joi.textField()
      .required()
      .messages({
        'any.required': lng('registration:taxNumberRequired'),
      }),
  })

export const utmValidation = Joi.object<UTM>().pattern(
  Joi.utm().required(),
  Joi.string().allow('', null),
)
